import {
  useEffect, useMemo, useRef, useCallback,
} from 'react';
import { noop } from 'functions/noop';
import {
  PENDING, PRISTINE, FAILURE, SUCCESS,
} from '../constants/queryStatuses';
import { useSafeState } from './useSafeState';

const INIT_STATE = { status: PRISTINE, data: null };

const getParametersIdDefault = (params) => params;

// dataKey can be used to force action re-run; in most cases should be stable; can be empty
export const useAsyncAction = ({
  action,
  parameters,
  dataId,
  // to determine if the parameters have changed
  getParametersId = getParametersIdDefault,
  isActive,
}) => {
  const [state, setState] = useSafeState(INIT_STATE);
  const abortControllerRef = useRef(null);

  const effectId = useMemo(() => (
    `${dataId}_${getParametersId(parameters)}`
  ), [dataId, getParametersId, parameters]);

  const effect = useCallback((signal) => {
    const actionArgs = [{ signal }];
    if (parameters !== undefined) {
      actionArgs.unshift(parameters);
    }
    return action(...actionArgs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectId]);

  useEffect(() => {
    if (isActive || state === INIT_STATE) {
      return;
    }
    setState(INIT_STATE);
  }, [isActive, setState, state]);

  useEffect(() => {
    if (!isActive) {
      return noop;
    }

    let canceled = false;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const { signal } = abortController;

    setState({ data: null, status: PENDING });

    effect(signal)
      .then((result) => {
        if (canceled) {
          return;
        }
        setState({ data: result, status: SUCCESS });
      })
      .catch((ex) => {
        if (canceled) {
          return;
        }
        console.error(`useAsyncAction ${dataId} ex:`, ex);
        setState({ data: null, status: FAILURE });
      });

    return () => {
      canceled = true;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [dataId, effect, isActive, setState]);

  return isActive ? state : INIT_STATE;
};
